import axios from 'axios';

// Function to get detailed information about a specific store
export const getStoreDetails = async (storeId, chain = '') => {
  const t0 = performance.now();
  try {
    const url = chain
      ? `/api/store/${storeId}?chain=${encodeURIComponent(chain)}`
      : `/api/store/${storeId}`;
    console.log(`[getStoreDetails] GET ${url}`);
    const response = await axios.get(url);
    console.log(`[getStoreDetails] done in ${(performance.now() - t0).toFixed(0)}ms`);
    return response.data;
  } catch (error) {
    console.error(`[getStoreDetails] failed after ${(performance.now() - t0).toFixed(0)}ms:`, error);
    
    // Fallback: /api/store-details/:storeId (spawns a Python process — slow!)
    try {
      console.warn(`[getStoreDetails] ⚠️ FALLBACK triggered at ${(performance.now() - t0).toFixed(0)}ms — this calls a Python subprocess and will be slow`);
      const detailsResponse = await axios.get(`/api/store-details/${storeId}`);
      console.warn(`[getStoreDetails] fallback done in ${(performance.now() - t0).toFixed(0)}ms`);
      return detailsResponse.data;
    } catch (fallbackError) {
      console.error(`[getStoreDetails] fallback also failed after ${(performance.now() - t0).toFixed(0)}ms:`, fallbackError);
      throw error;
    }
  }
};

// Function to search for a product across all stores
export const searchProductAcrossStores = async (searchTerm, maxDistance = null, homeLocation = null) => {
  try {
    const params = new URLSearchParams({ term: searchTerm });
    if (maxDistance !== null) params.set('maxDistance', maxDistance);
    if (homeLocation) {
      params.set('homeLat', homeLocation.lat);
      params.set('homeLon', homeLocation.lon);
    }
    const url = `/api/search-product?${params}`;
    console.log(`Searching for product "${searchTerm}" across all stores${maxDistance ? ` (within ${maxDistance}km)` : ''}...`);
    const response = await axios.get(url);
    console.log(`Found ${response.data.length} matching products across all stores`);
    return response.data;
  } catch (error) {
    console.error('Error searching for product:', error);
    return [];
  }
};

// Module-level cache: all callers share one in-flight request and the result is reused
let _storesCache = null;
let _storesFetch = null;

export const getAvailableStores = async () => {
  if (_storesCache) return _storesCache;
  if (!_storesFetch) {
    _storesFetch = axios.get('/api/stores')
      .then(r => { _storesCache = r.data; return r.data; })
      .catch(err => { _storesFetch = null; console.error('Error fetching available stores:', err); return []; });
  }
  return _storesFetch;
};

// Function to load price data for a specific store
export const loadStoreData = async (storeId) => {
  try {
    console.log(`Fetching data for store ${storeId} from API...`);
    const response = await axios.get(`/api/prices/${storeId}`);
    console.log(`Successfully loaded data for store ${storeId} from API, found ${response.data.length} items`);
    
    // Log the first item to see the structure
    if (response.data.length > 0) {
      console.log('First item structure:', response.data[0]);
    }
    
    return response.data;
  } catch (error) {
    console.error(`Error fetching data for store ${storeId} from API:`, error);
    return [];
  }
};

// Function to compare prices between two stores
export const comparePrices = (store1Data, store2Data) => {
  console.log('Starting price comparison...');
  console.log(`Store 1 data: ${store1Data.length} items`);
  console.log(`Store 2 data: ${store2Data.length} items`);
  
  // Check if we have valid data
  if (!Array.isArray(store1Data) || !Array.isArray(store2Data)) {
    console.error('Invalid data format:', { store1Data, store2Data });
    return [];
  }
  
  // Log sample data to understand structure
  console.log('Store 1 sample item:', store1Data.length > 0 ? store1Data[0] : 'No items');
  console.log('Store 2 sample item:', store2Data.length > 0 ? store2Data[0] : 'No items');
  
  const comparisonMap = new Map();
  let store1ItemsProcessed = 0;
  let store1ValidItems = 0;
  
  // Process store1 data
  store1Data.forEach(item => {
    store1ItemsProcessed++;
    
    // Check if the item has the required fields
    if (!item) {
      console.warn('Null or undefined item in store1Data');
      return;
    }
    
    // Log every 1000th item to track progress
    if (store1ItemsProcessed % 1000 === 0) {
      console.log(`Processed ${store1ItemsProcessed}/${store1Data.length} items from store 1`);
    }
    
    if (item.ItemCode && item.ItemName && item.ItemPrice) {
      store1ValidItems++;
      comparisonMap.set(item.ItemCode, {
        itemCode: item.ItemCode,
        itemName: item.ItemName,
        store1Price: parseFloat(item.ItemPrice),
        store1UnitPrice: item.UnitOfMeasurePrice ? parseFloat(item.UnitOfMeasurePrice) : null,
        store1UnitMeasure: item.UnitOfMeasure || null,
        store1CanonicalPrice: item.CanonicalPrice ? parseFloat(item.CanonicalPrice) : null,
        store1CanonicalUnit: item.CanonicalUnit || null,
        store2Price: null,
        store2UnitPrice: null,
        store2UnitMeasure: null,
        store2CanonicalPrice: null,
        store2CanonicalUnit: null,
        priceDifference: null,
        percentageDifference: null
      });
    }
  });
  
  console.log(`Store 1 processing complete: ${store1ValidItems} valid items out of ${store1ItemsProcessed} total`);
  
  let store2ItemsProcessed = 0;
  let store2ValidItems = 0;
  let matchingItems = 0;
  
  // Process store2 data and update comparison
  store2Data.forEach(item => {
    store2ItemsProcessed++;
    
    // Check if the item has the required fields
    if (!item) {
      console.warn('Null or undefined item in store2Data');
      return;
    }
    
    // Log every 1000th item to track progress
    if (store2ItemsProcessed % 1000 === 0) {
      console.log(`Processed ${store2ItemsProcessed}/${store2Data.length} items from store 2`);
    }
    
    if (item.ItemCode && item.ItemPrice) {
      store2ValidItems++;
      const existingItem = comparisonMap.get(item.ItemCode);
      
      if (existingItem) {
        // Item exists in both stores
        matchingItems++;
        const store2Price = parseFloat(item.ItemPrice);
        const store2CanonicalPrice = item.CanonicalPrice ? parseFloat(item.CanonicalPrice) : null;
        const store2CanonicalUnit = item.CanonicalUnit || null;

        // Prefer canonical (₪/kg, ₪/L, or ₪/unit) whenever both stores have
        // it.  Same barcode can be sold in different pack sizes (150g retail
        // pack vs by-weight kilogram) — raw ItemPrice diffs are meaningless
        // in that case, canonical strips the pack size out.
        let basePrice1, basePrice2;
        if (existingItem.store1CanonicalPrice != null && store2CanonicalPrice != null) {
          basePrice1 = existingItem.store1CanonicalPrice;
          basePrice2 = store2CanonicalPrice;
        } else {
          basePrice1 = existingItem.store1Price;
          basePrice2 = store2Price;
        }
        const priceDifference = basePrice1 - basePrice2;
        const percentageDifference = basePrice1 > 0
          ? (priceDifference / basePrice1) * 100
          : 0;

        comparisonMap.set(item.ItemCode, {
          ...existingItem,
          store2Price: store2Price,
          store2UnitPrice: item.UnitOfMeasurePrice ? parseFloat(item.UnitOfMeasurePrice) : null,
          store2UnitMeasure: item.UnitOfMeasure || null,
          store2CanonicalPrice: store2CanonicalPrice,
          store2CanonicalUnit: store2CanonicalUnit,
          priceDifference: priceDifference,
          percentageDifference: percentageDifference
        });
      } else {
        // Item only exists in store2
        comparisonMap.set(item.ItemCode, {
          itemCode: item.ItemCode,
          itemName: item.ItemName,
          store1Price: null,
          store1UnitPrice: null,
          store1UnitMeasure: null,
          store1CanonicalPrice: null,
          store1CanonicalUnit: null,
          store2Price: parseFloat(item.ItemPrice),
          store2UnitPrice: item.UnitOfMeasurePrice ? parseFloat(item.UnitOfMeasurePrice) : null,
          store2UnitMeasure: item.UnitOfMeasure || null,
          store2CanonicalPrice: item.CanonicalPrice ? parseFloat(item.CanonicalPrice) : null,
          store2CanonicalUnit: item.CanonicalUnit || null,
          priceDifference: null,
          percentageDifference: null
        });
      }
    } else {
      console.warn('Invalid item in store2Data:', item);
    }
  });
  
  console.log(`Store 2 processing complete: ${store2ValidItems} valid items out of ${store2ItemsProcessed} total`);
  console.log(`Found ${matchingItems} items that exist in both stores`);
  
  // Convert map to array and filter for items in both stores
  const result = Array.from(comparisonMap.values())
    .filter(item => item.store1Price !== null && item.store2Price !== null)
    .sort((a, b) => Math.abs(b.percentageDifference) - Math.abs(a.percentageDifference));
  
  console.log(`Final comparison result: ${result.length} items`);
  
  return result;
};

// Function to browse all products in a store from the barcode index
export const getStoreInventory = async (storeId, chain, { page = 1, pageSize = 50, search = '', promotionsOnly = false, sortBy = '' } = {}) => {
  const t0 = performance.now();
  try {
    const params = new URLSearchParams({ chain, page, pageSize });
    if (search) params.set('search', search);
    if (promotionsOnly) params.set('promotionsOnly', 'true');
    if (sortBy) params.set('sortBy', sortBy);
    const url = `/api/store-inventory/${storeId}?${params}`;
    console.log(`[getStoreInventory] GET ${url}`);
    const response = await axios.get(url);
    console.log(`[getStoreInventory] done in ${(performance.now() - t0).toFixed(0)}ms, total=${response.data.total}`);
    return response.data;
  } catch (error) {
    console.error(`[getStoreInventory] failed after ${(performance.now() - t0).toFixed(0)}ms:`, error);
    return { total: 0, page, pageSize, items: [] };
  }
};

// Function to search for products by name
export const searchProducts = (data, searchTerm) => {
  if (!searchTerm || searchTerm.trim() === '') {
    return data;
  }
  
  const normalizedSearchTerm = searchTerm.toLowerCase();
  
  return data.filter(item => 
    item.itemName && item.itemName.toLowerCase().includes(normalizedSearchTerm)
  );
};
